/**
 * File Name          : physens-parser.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { Observable } from "rxjs";
import { Packet, PACKET_SUB_TYPE, PACKET_TYPE, Publisher, ResponsePacket } from "../models/index";
import { OutputDatum, OutputsetPacket } from "../models/packets/outputset-packet.model";
import { ConnectionStatistics } from "../models/physens-devices/physens-device.model";

export interface ParsedValue { sampleTime_uS: number; values: (number | bigint)[][] }
export type ParsedValues = { [pubId: string]: ParsedValue[] };

export function parseSNSBytes(inputByteBuffer: Uint8Array): Observable<{progress: number, parsedData?: {connectionStats: ConnectionStatistics, publishers: Publisher[], device_info: any, parsedData: ParsedValues}} > {
  return new Observable(subscriber => {

    let totalLength = inputByteBuffer.length

    let byteBuffer = new Uint8Array(inputByteBuffer)
    let connectionStats = new ConnectionStatistics();
    let publishers: Publisher[] = [];
    let publisherById = new Map<number, Publisher>();
    let lastValidPktNum = 0;
    let lastValidByteIdx = 0;
    const syncPublisherMap = () => {
      publisherById.clear();
      publishers.forEach((publisher) => {
        publisherById.set(publisher.pubId, publisher);
      });
    };
    let device_info: any = []
    let parsedData: ParsedValues = {}
    let progress = 0;

    let offset = 0;

    while(true) {
      let startIndex = -1;
      for(let i = offset; i < byteBuffer.length; i++) {
        if(byteBuffer[i] === 0xCC) {
          startIndex = i;
          break;
        }
      }

      if(startIndex < 0) {
        subscriber.next({
          progress: 100,
          parsedData: {
            connectionStats,
            publishers,
            device_info,
            parsedData
          }
        })
        subscriber.complete();
        break;
      }

      connectionStats.discarded_bytes += (startIndex - offset)

      offset = startIndex;
      let newprogress = ((offset) / totalLength) * 100
      if(Math.floor(newprogress) > Math.floor(progress)) {
        progress = newprogress
        subscriber.next({
          progress: progress,
        });
      }
      
      if(byteBuffer.length - offset > 8) {
        var payloadLen = byteBuffer[offset + 1]
        let packetSize = payloadLen * 4 + 8;

        if(byteBuffer.length - offset >= packetSize) {

          var pkt = Packet.fromBytes(byteBuffer.slice(offset, offset + packetSize))

          if(pkt.validCRC) {
            connectionStats.valid_packets += 1;
            let throwawayBytes = offset - lastValidByteIdx;
            if(lastValidPktNum != 0) {
              let missedPkts = pkt.mPacketNumber! - lastValidPktNum -1;
              connectionStats.missed_packets += missedPkts;
              if(missedPkts != 0) {
                console.warn(`Missed ${missedPkts} packets between packet number ${lastValidPktNum} and ${pkt.mPacketNumber!}. Throwaway bytes since last valid packet: ${throwawayBytes}`);
              }
            }
            lastValidPktNum = pkt.mPacketNumber!

            
          

            lastValidByteIdx = offset + packetSize - 1;
            

            offset += packetSize;
            switch(pkt.mPacketType) {
              case PACKET_TYPE.RESPONSE: {
                var [success, resp_pkt] = ResponsePacket.fromPacket(pkt, publishers)
                if(success) {
                //   this.packetParsed(resp_pkt)

                  if(resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_DEVINFO) {
                    device_info = {
                      deviceSN: resp_pkt.deviceSN!,
                      projectID: resp_pkt.projectID!,
                      deviceType: resp_pkt.deviceType!,
                      roleID: resp_pkt.roleID!,
                      appSpecInfo: resp_pkt.appSpecInfo!,
                      fwMajorVersion: resp_pkt.fwMajorVersion!,
                      fwMinorVersion: resp_pkt.fwMinorVersion!,
                      fwBugVersion: resp_pkt.fwBugVersion!,
                      hwProductID: resp_pkt.hwProductID!,
                      hwMajorVersion: resp_pkt.hwMajorVersion!,
                      hwMinorVersion: resp_pkt.hwMinorVersion!,
                      compileTime: resp_pkt.compileTime!,
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PUBLISHERS) {
                    publishers = resp_pkt.publishers
                    syncPublisherMap()
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PUBLISHER_NAME) {
                    let pub = publisherById.get(resp_pkt.pubId!)
                    if(pub != null) {
                      pub.name = resp_pkt.pubName!
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUTS) {
                    let pub = publisherById.get(resp_pkt.pubId!)
                    if(pub != null) {
                      pub.outputs = resp_pkt.outputList
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUT_NAME) {
                    let pub = publisherById.get(resp_pkt.pubId!)
                    if(pub != null) {
                      let output = pub.outputs.find((value, index, obj)=> {return value.index == resp_pkt.outputIdx})
                      if(output != null) {
                        output.name = resp_pkt.outputName!
                      }
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUT_UNITS) {
                    let pub = publisherById.get(resp_pkt.pubId!)
                    if(pub != null) {
                      let output = pub.outputs.find((value, index, obj)=> {return value.index == resp_pkt.outputIdx})
                      if(output != null) {
                        output.units = resp_pkt.outputUnits!
                      }
                    }
                  }
                }
                break;
              }
              case PACKET_TYPE.OUTPUT_SET: {
                var [success, oset_pkt] = OutputsetPacket.fromPacket(pkt, publishers)
                if(success) {
                  oset_pkt.data.forEach((datum: OutputDatum)=> {
                    if(!parsedData[datum.pubId]) {
                        parsedData[datum.pubId] = []
                    }
                    parsedData[datum.pubId].push({
                        sampleTime_uS: datum.sampleTime_us,
                        values: datum.value
                    })
                  })
                }
                break;
              }
            //   case PACKET_TYPE.APP_RESPONSE: {
            //     var [success, appresp_pkt] = AppResponsePacket.fromPacket(pkt)
            //     if(success) {
            //       this.packetParsed(appresp_pkt)
            //     }
            //     break;
            //   }
            //   case PACKET_TYPE.APP_STATUS: {
            //     var [success, appstat_pkt] = AppStatusPacket.fromPacket(pkt)
            //     if(success) {
            //       this.packetParsed(appstat_pkt)
            //     }
            //     break;
            //   }
            //   default:
            //     this.packetParsed(pkt)
            //     break;
            }


          } else {
            Object.entries(parsedData).forEach(([pubId, values]) => {
              if(values.length > 0) {
                console.log(`Publisher ${pubId}: ${values[values.length - 1].sampleTime_uS}`);
              }
            });

            console.log("Next index of CC in invalid CRC Packet" + pkt.mBytes?.slice(1).findIndex(byte => byte === 0xCC));
            connectionStats.discarded_bytes += 1
            connectionStats.invalid_CRC_packets += 1
            offset += 1
          }

        } else {
          break;
        }
      } else {
        break;
      }

      
    }
    subscriber.next({
      progress: 100,
      parsedData: {
        connectionStats,
        publishers,
        device_info,
        parsedData
      }
    });
    subscriber.complete();
  });
}